import React, { useState, useEffect } from 'react';
import { Phone, PhoneMissed, MessageSquare, AlertTriangle, Check, Clock, Shield, ChevronDown, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_BADGE = {
  ringing: 'bg-yellow-600 text-white',
  answered: 'bg-green-600 text-white',
  missed: 'bg-red-600 text-white',
  completed: 'bg-gray-600 text-gray-200',
};

const PIPELINE_BADGE = {
  received: 'bg-gray-700 text-gray-300',
  transcribing: 'bg-blue-900/50 text-blue-300',
  extracting: 'bg-purple-900/50 text-purple-300',
  conflict_check: 'bg-amber-900/50 text-amber-300',
  matter_opened: 'bg-green-900/50 text-green-300',
  complete: 'bg-teal-900/50 text-teal-300',
  stopped_conflict: 'bg-red-900/50 text-red-300',
};

const CONFLICT_BADGE = {
  clear: { bg: 'bg-green-900/40', text: 'text-green-300', label: '✅ Clear' },
  potential: { bg: 'bg-amber-900/40', text: 'text-amber-300', label: '⚠️ Potential' },
  conflict: { bg: 'bg-red-900/40', text: 'text-red-300', label: '🔴 Conflict' },
};

const IntakeDashboard = () => {
  const [stats, setStats] = useState({});
  const [calls, setCalls] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today');
  const navigate = useNavigate();

  const fetchAll = async () => {
    // /phone-intake/* (A2): the old /intake/* paths were shadowed by the
    // staff router's GET /intake/{intake_id} and 404'd, and the Promise.all
    // meant one 404 blanked every widget. allSettled keeps each independent.
    try {
      setLoading(true);
      const [statsR, callsR, conflictsR] = await Promise.allSettled([
        api.get('/phone-intake/stats'),
        api.get('/phone-intake/calls/today'),
        api.get('/phone-intake/conflicts/pending'),
      ]);
      if (statsR.status === 'fulfilled') setStats(statsR.value.data || {});
      else console.error('phone-intake stats failed', statsR.reason);
      if (callsR.status === 'fulfilled') setCalls(callsR.value.data || []);
      else console.error('phone-intake calls failed', callsR.reason);
      if (conflictsR.status === 'fulfilled') setConflicts(conflictsR.value.data || []);
      else console.error('phone-intake conflicts failed', conflictsR.reason);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 30000); return () => clearInterval(i); }, []);

  const handleClearConflict = async (id) => {
    try {
      await api.post(`/phone-intake/conflicts/${id}/clear`);
      setConflicts(prev => prev.filter(c => c.id !== id));
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-8 text-gray-500 animate-pulse">Loading intake data...</div>;

  const missed = calls.filter(c => c.status === 'missed');

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <h1 className="text-xl font-bold text-gray-200 flex items-center gap-2">
        <Phone className="w-5 h-5 text-teal-400" /> Intake Pipeline
      </h1>

      {/* Stat cards */}
      <div className="grid grid-cols-6 gap-3">
        <StatCard label="Today" value={stats.today_total || 0} color="text-gray-200" />
        <StatCard label="Answered" value={stats.today_answered || 0} color="text-green-400" />
        <StatCard label="Missed" value={stats.today_missed || 0} color="text-red-400" />
        <StatCard label="Week Completed" value={stats.week_completed || 0} color="text-teal-400" />
        <StatCard label="Matters Opened" value={stats.week_matters_opened || 0} color="text-blue-400" />
        <StatCard label="Conflicts" value={stats.week_conflicts || 0} color="text-amber-400" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-700">
        {[
          { id: 'today', label: 'Live / Today' },
          { id: 'conflicts', label: `Pending Review (${conflicts.length})` },
          { id: 'missed', label: `Missed (${missed.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm border-b-2 transition ${tab === t.id ? 'border-teal-400 text-teal-300' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Today tab */}
      {tab === 'today' && (
        <div className="space-y-2">
          {calls.length === 0 && (
            <div className="text-center py-8"><Phone className="w-8 h-8 text-gray-600 mx-auto mb-2" /><p className="text-gray-500 text-sm">No calls today</p></div>
          )}
          {calls.map(call => (
            <CallCard key={call.call_sid} call={call} navigate={navigate} />
          ))}
        </div>
      )}

      {/* Conflicts tab */}
      {tab === 'conflicts' && (
        <div className="space-y-2">
          {conflicts.length === 0 && <p className="text-gray-500 text-sm text-center py-8">No pending conflicts</p>}
          {conflicts.map(c => {
            const cb = CONFLICT_BADGE[c.conflict_type] || CONFLICT_BADGE.potential;
            return (
              <div key={c.id} className={`rounded-lg border p-4 ${cb.bg} border-gray-700`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span className={`text-sm font-medium ${cb.text}`}>{cb.label}</span>
                    <span className="text-xs text-gray-500">Call: {c.call_sid}</span>
                  </div>
                  <button onClick={() => handleClearConflict(c.id)} className="flex items-center gap-1 px-3 py-1 rounded text-xs bg-green-700 hover:bg-green-600 text-white">
                    <Shield className="w-3 h-3" /> Clear Conflict
                  </button>
                </div>
                {c.matches?.map((m, i) => (
                  <div key={i} className="text-xs text-gray-400 ml-6 mt-1">
                    <b>{m.party_checked}</b> → {m.match_found} ({m.match_type}, {Math.round(m.confidence * 100)}%)
                    {m.case_id && <span className="text-gray-600 ml-2">case: {m.case_id}</span>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Missed tab */}
      {tab === 'missed' && (
        <div className="space-y-2">
          {missed.length === 0 && <p className="text-gray-500 text-sm text-center py-8">No missed calls today</p>}
          {missed.map(call => (
            <div key={call.call_sid} className="bg-red-900/10 border border-red-800/30 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <PhoneMissed className="w-4 h-4 text-red-400" />
                <span className="text-sm text-gray-200">{call.from_number}</span>
                <span className="text-xs text-gray-500">{call.started_at ? new Date(call.started_at).toLocaleTimeString() : ''}</span>
              </div>
              <span className="text-xs text-gray-500">SMS sent</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 text-center">
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
  </div>
);

const CallCard = ({ call, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const intake = call.intake_data || {};
  const prospect = intake.prospect || {};
  const matter = intake.matter || {};
  const lead = intake.lead_quality || {};
  const conflict = call.conflict_check || {};
  const cb = CONFLICT_BADGE[conflict.type] || {};

  return (
    <div className="bg-gray-900/60 rounded-lg border border-gray-700 hover:border-gray-600 transition">
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          {call.source === 'sms' ? <MessageSquare className="w-4 h-4 text-blue-400" /> : <Phone className="w-4 h-4 text-teal-400" />}
          <span className="text-sm text-gray-200">{prospect.name || call.from_number}</span>
          {matter.matter_type && <span className="text-xs text-gray-400">{matter.matter_type}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE[call.status] || ''}`}>{call.status}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${PIPELINE_BADGE[call.pipeline_status] || ''}`}>{call.pipeline_status}</span>
          {conflict.type && conflict.type !== 'unknown' && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${cb.bg || ''} ${cb.text || ''}`}>{cb.label}</span>
          )}
          {lead.score && <span className="text-[10px] text-gray-500">{lead.score}/10</span>}
          <span className="text-[10px] text-gray-600">{call.started_at ? new Date(call.started_at).toLocaleTimeString() : ''}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-gray-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-800 pt-2 text-xs space-y-1">
          {matter.facts_summary && <p className="text-gray-400">{matter.facts_summary}</p>}
          {matter.urgency && <div className="text-gray-500">Urgency: <span className="text-gray-300">{matter.urgency}</span></div>}
          {lead.fee_potential && <div className="text-gray-500">Fee potential: <span className="text-gray-300">{lead.fee_potential}</span></div>}
          {call.duration_seconds && <div className="text-gray-500">Duration: {Math.floor(call.duration_seconds / 60)}m {call.duration_seconds % 60}s</div>}
          {call.case_id && (
            <button onClick={() => navigate(`/cases/${call.case_id}`)} className="flex items-center gap-1 text-teal-400 hover:text-teal-300 mt-1">
              <ExternalLink className="w-3 h-3" /> View Case
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default IntakeDashboard;
